import React, { useCallback, useEffect, useState } from "react";
import api from "../../../../api/client";
import Toast from "../../components/Toast";
import Tabs from "../../components/Tabs";

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d6dfed",
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#4f6280", marginBottom: 4 };

const MASTERS = {
  categories: { path: "categories", singular: "Category", fields: [] },
  locations: { path: "locations", singular: "Location", fields: [] },
  suppliers: {
    path: "suppliers", singular: "Supplier",
    fields: [
      { key: "contactName", label: "Contact Name" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
    ],
  },
};

function MasterModal({ config, item, onClose, onSaved }) {
  const [name, setName] = useState(item?.name || "");
  const [extra, setExtra] = useState(() =>
    Object.fromEntries(config.fields.map((f) => [f.key, item?.[f.key] || ""]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { name: name.trim(), ...extra };
      if (item) {
        await api.patch(`/manufacturing/inventory/${config.path}/${item.id}`, payload);
      } else {
        await api.post(`/manufacturing/inventory/${config.path}`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 28, width: 420, maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
        <h2 style={{ margin: "0 0 20px", fontSize: 18, color: "#0f2d5c" }}>
          {item ? `Edit ${config.singular}` : `Add ${config.singular}`}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          {config.fields.map((f) => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{f.label}</label>
              <input
                style={inputStyle}
                value={extra[f.key]}
                onChange={(e) => setExtra((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            </div>
          ))}
          {error && <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: "pointer", fontSize: 14 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, background: "#1d4ed8", color: "#fff", border: "none", cursor: saving ? "default" : "pointer", fontWeight: 700, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MasterTab({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/manufacturing/inventory/${config.path}`);
      setItems(resp.data.data || []);
    } catch {
      setToast({ msg: `Failed to load ${config.singular.toLowerCase()}s`, type: "error" });
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/manufacturing/inventory/${config.path}/${id}`);
      setToast({ msg: `${config.singular} deleted`, type: "success" });
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to delete", type: "error" });
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="btn-primary" onClick={() => setModal("new")}>+ Add {config.singular}</button>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No {config.singular.toLowerCase()}s yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>Name</th>
                {config.fields.map((f) => (
                  <th key={f.key} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5" }}>{f.label}</th>
                ))}
                <th style={{ padding: "10px 16px", borderBottom: "1px solid #e4ebf5", width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} style={{ borderBottom: idx < items.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <td style={{ padding: "10px 16px", fontWeight: 600, color: "#0f2d5c" }}>{it.name}</td>
                  {config.fields.map((f) => (
                    <td key={f.key} style={{ padding: "10px 16px", color: "#64748b" }}>{it[f.key] || "—"}</td>
                  ))}
                  <td style={{ padding: "10px 16px", textAlign: "right" }}>
                    <button type="button" onClick={() => setModal(it)} style={{ background: "none", border: "1px solid #d6dfed", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: "#374151", marginRight: 6 }}>
                      Edit
                    </button>
                    <button type="button" onClick={() => setDeleteConfirm(it)} style={{ background: "none", border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: "#dc2626" }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <MasterModal
          config={config}
          item={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); setToast({ msg: `${config.singular} saved`, type: "success" }); }}
        />
      )}

      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 360, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 8px", color: "#0f2d5c" }}>Delete {config.singular}?</h3>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#64748b" }}>
              Delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} style={{ padding: "8px 16px", borderRadius: 8, background: "#dc2626", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

const TABS = [
  { key: "categories", label: "Categories" },
  { key: "locations", label: "Locations" },
  { key: "suppliers", label: "Suppliers" },
];

export default function InventorySettings() {
  const [tab, setTab] = useState("categories");
  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <div style={{ margin: "0 0 20px" }}>
        <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Inventory Settings</h1>
        <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Manage categories, locations and suppliers used by Inventory.</p>
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <MasterTab key={tab} config={MASTERS[tab]} />
    </div>
  );
}
